import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Lock, RotateCcw } from 'lucide-react';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { cn } from '../util/designUtil';
import { formatSubscribeCta } from '../util/subscriptionPrice';
import { SubscribeFailure, SubscriptionAccessState, resumeNotice } from '../util/subscriptionState';

interface SubscribeCardProps {
  creatorName: string;
  /** null when this creator is not taking subscriptions. */
  priceCredits: number | null;
  state: SubscriptionAccessState;
  /** End of the paid period, already formatted; null when unknown. */
  periodEnd: string | null;
  busy: boolean;
  error: SubscribeFailure | null;
  onSubscribe: () => void;
  onCancel: () => void;
  onResume: () => void;
}

/**
 * The subscribe / subscribed / cancelled panel above a creator's timeline.
 * Presentational: every decision is made by the container.
 */
const SubscribeCard: React.FC<SubscribeCardProps> = ({
  creatorName,
  priceCredits,
  state,
  periodEnd,
  busy,
  error,
  onSubscribe,
  onCancel,
  onResume,
}) => {
  const { theme } = useColorTheme();

  return (
    <div className="w-full max-w-full bg-white p-5 sm:p-6 rounded-xl shadow-lg border border-gray-200">
      {state === 'active' ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              <Check className={cn('h-4 w-4', theme.secondary)} />
              Subscribed
            </p>
            <p className="text-xs text-gray-600 break-words">
              You can see {creatorName}&rsquo;s subscribers-only posts
              {periodEnd ? `. Renews ${periodEnd}.` : '.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="w-full sm:w-auto shrink-0 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {busy ? 'Working…' : 'Cancel'}
          </button>
        </div>
      ) : state === 'cancelled' ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">Cancelled</p>
            <p className="text-xs text-gray-600 break-words">
              {periodEnd
                ? `Cancelled — you keep access until ${periodEnd}.`
                : 'Cancelled — you keep access until the end of your current period.'}
            </p>
            <p className="text-xs text-gray-500 break-words">{resumeNotice(periodEnd)}</p>
          </div>
          <button
            type="button"
            onClick={onResume}
            disabled={busy}
            className={cn(
              'w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md text-sm font-semibold disabled:opacity-50',
              theme.accent,
            )}
          >
            {busy ? (
              'Working…'
            ) : (
              <>
                <RotateCcw className="h-4 w-4" />
                Resume
              </>
            )}
          </button>
        </div>
      ) : priceCredits === null ? (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Subscriptions</p>
          <p className="text-xs text-gray-600 break-words">
            {creatorName} isn&rsquo;t taking subscriptions yet.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              Subscribe to {creatorName}
            </p>
            <p className="text-xs text-gray-600 break-words">
              See every subscribers-only post while your subscription is active.
            </p>
          </div>
          <button
            type="button"
            onClick={onSubscribe}
            disabled={busy}
            className={cn(
              'w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md text-sm font-semibold disabled:opacity-50',
              theme.accent,
            )}
          >
            {busy ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                Subscribing…
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" />
                {formatSubscribeCta(priceCredits)}
              </>
            )}
          </button>
        </div>
      )}

      {error && (
        <p className="mt-3 text-xs text-red-600 break-words">
          {error.message}
          {error.showBuyCredits && (
            <>
              {' '}
              <Link to="/buy-credits" className={cn('font-semibold underline', theme.secondary)}>
                Buy credits
              </Link>
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default SubscribeCard;
